<template>
	<section
		:class="isMobile ? 'white' : 'am-blue'"
		class="app-nav-bar d-flex flex-column flex-md-row justify-center"
	>
		<VFadeTransition hide-on-leave>
			<button
				v-if="isMobile && !menuExpanded"
				:aria-expanded="menuExpanded.toString()"
				aria-controls="app-nav-bar-menu"
				aria-label="Afficher le menu public"
				type="button"
				class="menu-btn d-block d-md-inline-block font-weight-bold text-uppercase pink active white--text"
				@click="toggleMenu"
			>
				Assuré

				<VIcon
					color="white"
					size="20px"
					class="ml-1 mr-n1"
				>
					{{ downMenu }}
				</VIcon>
			</button>
		</VFadeTransition>

		<VFadeTransition hide-on-leave>
			<div
				v-if="isMobile && menuExpanded"
				class="d-flex justify-end white"
			>
				<button
					aria-controls="app-nav-bar-menu"
					aria-label="Fermer le menu public"
					type="button"
					class="close-btn d-flex flex-column align-center text-uppercase text-caption black--text"
					@click="toggleMenu"
				>
					<VIcon
						size="18px"
						color="black"
					>
						{{ closeIcon }}
					</VIcon>

					Fermer
				</button>
			</div>
		</VFadeTransition>

		<transition name="container">
			<div
				v-show="!isMobile || menuExpanded"
				id="app-nav-bar-menu"
				:class="{ 'expanded': menuExpanded }"
				class="container d-flex flex-column flex-md-row align-stretch align-md-center justify-space-between am-blue w-100"
			>
				<ul class="tab-menu d-flex flex-column flex-md-row pl-0 mr-0 mr-md-1">
					<li>
						<a
							href="https://www.ameli.fr/assure"
							class="menu-btn d-block d-md-inline-block font-weight-bold text-uppercase pink active white--text"
						>
							Assuré
						</a>
					</li>

					<li>
						<VMenu
							attach
							offset-y
							open-on-hover
							tile
							max-width="305px"
							transition="slide-y-transition"
						>
							<template #activator="{ on, attrs }">
								<button
									v-bind="attrs"
									type="button"
									class="menu-btn d-block d-md-inline-block font-weight-bold text-uppercase cyan white--text"
									v-on="on"
								>
									Professionnels de santé

									<VIcon
										color="white"
										size="20px"
										class="ml-1 mr-n1"
									>
										{{ downArrow }}
									</VIcon>
								</button>
							</template>

							<VSheet tag="ul">
								<li
									v-for="(item, index) in healthProLinks"
									:key="index"
								>
									<a
										:href="item.href"
										:data-text="item.label"
									>
										<VIcon
											color="cyan"
											size="20px"
											class="mr-1"
										>
											{{ rightArrow }}
										</VIcon>

										{{ item.label }}
									</a>
								</li>
							</VSheet>
						</VMenu>
					</li>

					<li>
						<a
							href="https://www.ameli.fr/entreprise"
							class="menu-btn d-block d-md-inline-block font-weight-bold text-uppercase yellow white--text"
						>
							Entreprise
						</a>
					</li>
				</ul>

				<nav
					aria-label="Menu institutionnel"
					class="ml-0 ml-md-1 ml-0 mr-md-2"
				>
					<component
						:is="isMobile ? 'VMenu' : 'div'"
						attach
						offset-y
						tile
						max-width="305px"
						transition="slide-y-transition"
					>
						<template #activator="{ on, attrs }">
							<button
								v-bind="attrs"
								type="button"
								class="menu-btn d-block d-md-inline-block font-weight-bold text-uppercase am-blue white--text"
								v-on="on"
							>
								L'assurance maladie

								<VIcon
									color="white"
									size="20px"
									class="ml-1 mr-n1"
								>
									{{ downArrow }}
								</VIcon>
							</button>
						</template>

						<ul
							:class="isMobile ? 'white' : 'pl-0'"
							class="d-flex flex-wrap flex-column flex-md-row mt-n1"
						>
							<li
								v-for="(item, index) in institutionalMenuLinks"
								:key="index"
							>
								<a
									:class="{ 'white--text': !isMobile }"
									:href="item.href"
									target="_blank"
									rel="noopener noreferrer"
								>
									<VIcon
										v-if="isMobile"
										color="am-blue"
										size="20px"
										class="mr-1"
									>
										{{ rightArrow }}
									</VIcon>

									{{ item.label }}
								</a>
							</li>
						</ul>
					</component>
				</nav>
			</div>
		</transition>
	</section>
</template>

<script lang="ts">
	import Vue from 'vue';
	import Component from 'vue-class-component';

	import { healthProLinks, institutionalMenuLinks } from './menuLinks';

	import { VMenu } from 'vuetify/lib/components';

	import { mdiChevronDown, mdiMenuDown, mdiCheckBold, mdiChevronRight, mdiClose } from '@mdi/js';

	@Component({
		components: {
			VMenu
		}
	})
	export default class OnGuardMessageCard extends Vue {
		healthProLinks = healthProLinks;
		institutionalMenuLinks = institutionalMenuLinks;
		checkIcon = mdiCheckBold;

		downArrow = mdiChevronDown;
		downMenu = mdiMenuDown;
		rightArrow = mdiChevronRight;
		closeIcon = mdiClose;

		menuExpanded = false;

		get isMobile(): boolean {
			return this.$vuetify.breakpoint.smAndDown;
		}

		toggleMenu(): void {
			const content = document.getElementById('app-nav-bar-menu');

			if (!content) {
				return;
			}

			if (content.style.maxHeight) {
				content.style.removeProperty('max-height');
				this.menuExpanded = false;
				return;
			}

			content.style.maxHeight = content.scrollHeight + 'px';
			this.menuExpanded = true;
		}
	}
</script>

<style lang="scss" scoped>
	@import '@cnamts/design-tokens/dist/tokens';

	$dark-grey: #2b3443;

	.app-nav-bar {
		z-index: 3;

		ul {
			list-style: none;
		}

		.container {
			max-width: 1180px;
			transition: .2s ease-out;

			&.expanded {
				opacity: 1;
			}

			@media only screen and (max-width: 959px) {
				opacity: 0;
				max-height: 0;
				overflow: hidden;
				background: #4c4f50 !important;
			}
		}

		.container-leave-active {
			visibility: hidden;
		}

		.close-btn {
			padding: 2px;
			line-height: 1.25;
			font-size: .7rem !important;
		}
	}

	.menu-btn {
		position: relative;
		padding: 6px 12px;
		text-decoration: none;
		transition: background .2s;
		text-align: left;
		width: 100%;

		&:not(.active):not(:hover):not([aria-expanded="true"]) {
			background: transparent !important;
		}

		// Bottom triangle
		@media only screen and (min-width: 960px) {
			&.active::after {
				content: '';
				position: absolute;
				left: 50%;
				margin-left: -7px;
				bottom: -6px;
				width: 0;
				height: 0;
				border-style: solid;
				border-width: 7px 6px 0;
				border-color: transparent;
				border-top-color: inherit;
				pointer-events: none;
			}
		}

		+ .v-menu ul {
			padding: 15px 35px;

			li:not(:last-child) {
				margin-bottom: 10px;
			}

			@media only screen and (max-width: 960px) {
				padding: 5px 15px;

				li:not(:last-child) {
					margin-bottom: 5px;
				}
			}

			a {
				color: $dark-grey;
				text-decoration: none;

				&:hover {
					// Fake hover effect to avoid layout shift
					text-shadow: 0 0 .65px $dark-grey, 0 0 .65px $dark-grey;
				}

				&:not(:hover) .v-icon {
					color: $dark-grey !important;
				}
			}
		}
	}

	nav ul {
		// Vertical divider
		@media only screen and (min-width: 960px) {
			li:not(:last-child)::after {
				content: '|';
				color: #fff;
				margin: 0 10px;
			}
		}

		a {
			font-size: .95rem;
			position: relative;
			text-decoration: none;
			transition: none;

			// Bottom decoration
			&::after {
				content: '';
				display: block;
				position: absolute;
				bottom: -7px;
				left: 50%;
				right: 50%;
				width: 0;
				height: 4px;
				transition: .2s;
			}

			&:hover::after {
				left: 0;
				right: 0;
				width: 100%;
				background: #fff;
			}

			@media only screen and (max-width: 960px) {
				color: $dark-grey !important;
			}
		}
	}
</style>
